import { FaExclamationTriangle } from "react-icons/fa";
import React, {
  useRef,
  useEffect,
  useState,
  useMemo,
  useCallback
} from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";

/**
 * DocChatBot — nhúng ứng dụng DocChatBot (doc-chatbot) vào Portal CRM qua iframe.
 * Dùng cùng pattern SSO postMessage như DocSearch:
 *   Portal → postMessage { type: "SET_CRM_CONFIG", email } → DocChatBot iframe
 *   DocChatBot → postMessage { type: "DOC_BOT_READY" }         → Portal
 *   DocChatBot → postMessage { type: "SYNC_PATH", path }       → Portal
 */
const DocChatBot = ({ isOpenSidebar = true }) => {
  const iframeRef = useRef(null);
  const containerRef = useRef(null);
  const [iframeHeight, setIframeHeight] = useState(0);
  const { currentUser } = useSelector(state => state.auth);
  const email = currentUser?.email;
  const DocChatBotAppUrl = process.env.REACT_APP_DOC_CHATBOT_APP_URL;

  // Khôi phục last-visited path (cả pathname + query params để giữ đúng session)
  const targetPath = useMemo(() => {
    return localStorage.getItem("doc_bot_app_path") || "/";
  }, []);

  const { fontFamily, fontSize } = useMemo(() => {
    const computed = window.getComputedStyle(document.body);
    return {
      fontFamily: computed.fontFamily,
      fontSize: computed.fontSize
    };
  }, []);

  const fullIframeUrl = useMemo(() => {
    if (!email || !DocChatBotAppUrl) return "";
    // If DocChatBotAppUrl does not have http/https, URL might fail so ensure origin is correct
    // (though new URL usually requires an absolute base or valid URL)
    let url;
    try {
      url = new URL(`${DocChatBotAppUrl}${targetPath}`);
    } catch {
      url = new URL(`${DocChatBotAppUrl}${targetPath}`, window.location.origin);
    }
    url.searchParams.set("isOpenSidebar", isOpenSidebar.toString());
    url.searchParams.set("email", email);
    if (fontFamily) url.searchParams.set("fontFamily", fontFamily);
    if (fontSize) url.searchParams.set("fontSize", fontSize);
    return url.toString();
  }, [email, DocChatBotAppUrl, targetPath, isOpenSidebar, fontFamily, fontSize]);

  // Tính iframeHeight trừ navbar + footer
  useEffect(() => {
    const calculateHeight = () => {
      const navbar =
        document.querySelector(".ptl-navbar-top") ||
        document.querySelector("nav.navbar") ||
        document.querySelector('[class*="navbar-top"]');
      const footer =
        document.querySelector("footer") ||
        document.querySelector('[class*="footer"]');

      const navbarHeight = navbar ? navbar.getBoundingClientRect().height : 0;
      const footerHeight = footer ? footer.getBoundingClientRect().height : 0;
      const available = window.innerHeight - navbarHeight - footerHeight - 20;
      setIframeHeight(available);
    };

    calculateHeight();
    window.addEventListener("resize", calculateHeight);
    return () => window.removeEventListener("resize", calculateHeight);
  }, []);

  // #region Author: ALD - Date: 02/04/2026 - Description: Lắng nghe messages từ DocChatBot iframe
  const sendConfig = useCallback(() => {
    if (iframeRef.current?.contentWindow && email && DocChatBotAppUrl) {
      iframeRef.current.contentWindow.postMessage(
        {
          type: "SET_CRM_CONFIG",
          email,
          fontFamily,
          fontSize,
          isOpenSidebar
        },
        new URL(DocChatBotAppUrl).origin
      );
    }
  }, [email, fontFamily, fontSize, DocChatBotAppUrl, isOpenSidebar]);
  
  useEffect(() => {
    const handleMessage = event => {
      if (!DocChatBotAppUrl) return;
      try {
        if (event.origin !== new URL(DocChatBotAppUrl).origin) return;
      } catch (_) {
        return;
      }

      const { type, path } = event.data || {};

      if (type === "SYNC_PATH" && path) {
        // Lưu cả path + query params (VD: /chat?session=abc123)
        localStorage.setItem("doc_bot_app_path", path);
      }

      // DOC_BOT_READY: DocChatBot đã mount listener — safe to send config
      if (type === "DOC_BOT_READY") {
        sendConfig();
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [DocChatBotAppUrl, sendConfig]);

  // #endregion

  // onLoad: gửi config ngay (trước khi DOC_BOT_READY có thể đến trước)
  const handleIframeLoad = () => {
    sendConfig();
  };

  if (!email) {
    return (
      <div
        style={{ height: iframeHeight || "80vh" }}
        className="tw-flex tw-items-center tw-justify-center tw-bg-slate-50"
      >
        <div className="tw-text-center tw-p-5">
          <h4 className="tw-text-slate-500 tw-mb-3">Authentication Required</h4>
          <p className="tw-text-slate-500">
            Please login to Portal to access the DocChatBot system.
          </p>
        </div>
      </div>
    );
  }

  if (!DocChatBotAppUrl) {
    return (
      <div
        style={{ height: iframeHeight || "80vh" }}
        className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center tw-p-4"
      >
        <div className="tw-text-red-600 tw-mb-3">
          <FaExclamationTriangle size={48} />
        </div>
        <h4 className="tw-font-bold">Doc ChatBot URL not configured</h4>
        <p className="tw-text-slate-500">
          Please set <code>REACT_APP_DOC_CHATBOT_APP_URL</code> in environment
          config.
        </p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="tw-p-1 tw-bg-white tw-rounded-md"
      style={{ width: "100%", height: iframeHeight || "80vh" }}
    >
      <iframe
        ref={iframeRef}
        src={fullIframeUrl}
        title="DocChatBot App"
        style={{
          width: "100%",
          height: "100%",
          border: "none",
          display: "block"
        }}
        allowFullScreen
        onLoad={handleIframeLoad}
      />
    </div>
  );
};

DocChatBot.propTypes = {
  isOpenSidebar: PropTypes.bool
};

export default DocChatBot;
